import { Component, ElementRef, HostListener, ViewChild, computed, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';

type CatalogItem = {
  title: string;
  icon: string;
  route?: string;
};

type CatalogGroup = {
  title: string;
  icon: string;
  items: CatalogItem[];
};

@Component({
  selector: 'app-catalog',
  imports: [RouterLink],
  templateUrl: './catalog.html',
  styleUrl: './catalog.css',
})
export class Catalog {
  constructor(private readonly router: Router) {}

  protected readonly catalogGroups: CatalogGroup[] = [
    {
      title: 'Bond Catalog',
      icon: 'bank',
      items: [
        { title: 'Bond Documentation', icon: 'document', route: '/catalogs/bond-documentation' },
        { title: 'Bond Proceed', icon: 'chart-line-star', route: '/catalogs/bond-proceed' },
        { title: 'Bond Proceed Subtype', icon: 'notepad-edit', route: '/catalogs/bond-proceed-subtype' },
        { title: 'Bond Type', icon: 'bookmark', route: '/catalogs/bond-type' },
        { title: 'Bond Format', icon: 'book-square', route: '/catalogs/bond-format' },
        { title: 'Bond Listing', icon: 'book-open', route: '/catalogs/bond-listing' },
        { title: 'Reoffer BMK', icon: 'bank', route: '/catalogs/reoffer-bmk' },
        { title: 'Non Named Dealer', icon: 'briefcase', route: '/catalogs/non-named-dealer' },
      ],
    },
    {
      title: 'Swaps',
      icon: 'arrow-right-left',
      items: [
        { title: 'Swap Action Type', icon: 'arrow-right-left', route: '/catalogs/swap-action-type' },
        { title: 'Swap Purpose', icon: 'arrows-loop', route: '/catalogs/swap-purpose' },
        { title: 'Swap Purpose Subtype', icon: 'notepad-bookmark', route: '/catalogs/swap-purpose-subtype' },
        { title: 'Swap Fees Type', icon: 'wallet', route: '/catalogs/swap-fee-type' },
      ],
    },
    {
      title: 'Geography',
      icon: 'geolocation',
      items: [
        { title: 'Country', icon: 'flag', route: '/catalogs/country' },
        { title: 'Region', icon: 'map', route: '/catalogs/region' },
      ],
    },
    {
      title: 'Investor',
      icon: 'people',
      items: [
        { title: 'Investor', icon: 'user', route: '/catalogs/investor' },
        { title: 'Investor Group', icon: 'users', route: '/catalogs/investor-group' },
        { title: 'Investor Type', icon: 'user-edit', route: '/catalogs/investor-type' },
      ],
    },
    {
      title: 'Invoices',
      icon: 'dollar',
      items: [
        { title: 'Vendor', icon: 'briefcase', route: '/catalogs/vendor' },
        { title: 'Vendor Type', icon: 'category', route: '/catalogs/vendor-type' },
        { title: 'Invoice Concept', icon: 'notepad', route: '/catalogs/invoice-concept' },
      ],
    },
    {
      title: 'Tools',
      icon: 'setting-2',
      items: [
        { title: 'Pending Items From', icon: 'calendar-search', route: '/catalogs/frida-config' },
        { title: 'Bonds Cloning', icon: 'copy', route: '/catalogs/bonds-cloning' },
        { title: 'Swaps Cloning', icon: 'copy-success', route: '/catalogs/swaps-cloning' },
      ],
    },
  ];

  protected readonly totalCount = this.catalogGroups.reduce((sum, group) => sum + group.items.length, 0);

  protected readonly searchTerm = signal('');

  protected readonly recentItems = signal<CatalogItem[]>(this.loadRecent());

  @ViewChild('searchInput')
  private searchInput?: ElementRef<HTMLInputElement>;

  protected readonly filteredGroups = computed<CatalogGroup[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) {
      return this.catalogGroups;
    }

    return this.catalogGroups
      .map((group) => ({
        ...group,
        items: group.title.toLowerCase().includes(term)
          ? group.items
          : group.items.filter((item) => item.title.toLowerCase().includes(term)),
      }))
      .filter((group) => group.items.length > 0);
  });

  protected readonly matchCount = computed(() =>
    this.filteredGroups().reduce((sum, group) => sum + group.items.length, 0),
  );

  @HostListener('document:keydown', ['$event'])
  protected onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) {
      return;
    }

    event.preventDefault();
    this.searchInput?.nativeElement.focus();
  }

  protected onSearch(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  protected clearSearch(): void {
    this.searchTerm.set('');
  }

  protected openFirstMatch(): void {
    if (!this.searchTerm().trim()) {
      return;
    }

    const first = this.filteredGroups()
      .flatMap((group) => group.items)
      .find((item) => !!item.route);
    if (first?.route) {
      this.rememberRecent(first);
      void this.router.navigateByUrl(first.route);
    }
  }

  protected highlight(title: string): { text: string; match: boolean }[] {
    const term = this.searchTerm().trim();
    const index = term ? title.toLowerCase().indexOf(term.toLowerCase()) : -1;
    if (index < 0) {
      return [{ text: title, match: false }];
    }

    return [
      { text: title.slice(0, index), match: false },
      { text: title.slice(index, index + term.length), match: true },
      { text: title.slice(index + term.length), match: false },
    ].filter((part) => part.text.length > 0);
  }

  protected rememberRecent(item: CatalogItem): void {
    if (!item.route) {
      return;
    }

    const next = [item, ...this.recentItems().filter((recent) => recent.route !== item.route)]
      .slice(0, Catalog.MAX_RECENT);
    this.recentItems.set(next);

    try {
      localStorage.setItem(Catalog.RECENT_STORAGE_KEY, JSON.stringify(next.map((recent) => recent.route)));
    } catch {
      // Storage may be unavailable (private mode / quota); recents are a convenience only.
    }
  }

  private static readonly RECENT_STORAGE_KEY = 'frida.catalogs.recent';
  private static readonly MAX_RECENT = 5;

  private loadRecent(): CatalogItem[] {
    try {
      const routes = JSON.parse(localStorage.getItem(Catalog.RECENT_STORAGE_KEY) ?? '[]') as unknown;
      if (!Array.isArray(routes)) {
        return [];
      }

      const byRoute = new Map(
        this.catalogGroups.flatMap((group) => group.items).filter((item) => item.route).map((item) => [item.route, item]),
      );
      return routes
        .map((route) => byRoute.get(String(route)))
        .filter((item): item is CatalogItem => !!item)
        .slice(0, Catalog.MAX_RECENT);
    } catch {
      return [];
    }
  }
}